import { ChangeDetectionStrategy, Component, EventEmitter, HostBinding, Input, OnChanges, Output, inject } from '@angular/core';

import {
    BetBuilderButtonSource,
    BetBuilderMarket,
    EventModel,
    EventOptionGroup,
    EventOptionGroupType,
} from '@frontend/sports/betting-offer/feature/model';
import { BetBuilderConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { ISimpleChanges } from '@frontend/sports/common/core/utils/simple-change';
import { SourceOfPick } from '@frontend/sports/option/pick/feature/tracking';
import { TabBarItem } from '@frontend/sports/web/tab-bar-utils';
import { DsNotificationBubble } from '@frontend/ui/notification-bubble';
import { DsTabsModule } from '@frontend/ui/tabsgroup';

import { ITabIndex, OptionGroupTabindexKeeperService, createDefaultTabIndex } from './option-group-tabindex-keeper.service';
import { PrecreatedBabOptionComponent } from './precreated-bab-option.component';

@Component({
    selector: 'ms-precreated-bab-option-group',
    templateUrl: './precreated-bab-option-group.component.html',
    styleUrls: ['./precreated-bab-option-group.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [PrecreatedBabOptionComponent, DsTabsModule, DsNotificationBubble],
})
export class PrecreatedBabOptionGroupComponent implements OnChanges {
    sitecore = inject(Sitecore);
    betBuilderConfig = inject(BetBuilderConfig);
    private tabKeeper = inject(OptionGroupTabindexKeeperService, { optional: true });

    @Input() event: EventModel;
    @Input() fixtureId: string;
    @Input() optionGroup: EventOptionGroup;
    @Input() tracking: string;
    @Input() trackingModuleType = '';
    @Input() showMarketHelp = false;
    @Input() showMoreCounter = false;
    @Input() optionsLimitOverride?: number;
    @Input() buttonSource?: BetBuilderButtonSource;
    @Input() betBuilderEpcotEnabled = false;
    @Input() hideBetBuilderButton: boolean;
    @Input() sourceOfPick: SourceOfPick;

    @Output() tabChange = new EventEmitter<number>();
    @Output() showAllRequested = new EventEmitter<void>();

    @HostBinding('class') className = 'precreated-build-a-bet';

    babTabs: TabBarItem[] = [];
    marketsList: BetBuilderMarket[];
    initialMarkets: number;
    activeGroup: EventOptionGroup;
    tabChanged = false;
    private tabIndex: ITabIndex = createDefaultTabIndex();

    constructor() {
        const betBuilderConfig = this.betBuilderConfig;

        this.initialMarkets = betBuilderConfig.initialPrecreatedMarketsPerSport;
    }

    ngOnChanges(changes: ISimpleChanges<PrecreatedBabOptionGroupComponent>): void {
        const group = changes.optionGroup;
        this.tabChanged = false;
        if (group) {
            this.handlePeriodTabs();
        }
    }

    setSubGroupTabSelected(set: string): void {
        const parsedSet = parseInt(set);

        this.tabChange.emit(parsedSet);
        this.tabChanged = true;
        this.setTabActivate(parsedSet);
    }

    private setTabActivate(active: number): void {
        this.babTabs.forEach((tab) => (tab.active = tab.id === active));
        this.babTabs = this.babTabs.slice(0);
        this.setOptionsActive(active);
    }

    private handlePeriodTabs(): void {
        if (this.optionGroup.hasSubGroups(EventOptionGroupType.Period)) {
            const tabIndex = this.getTabIndex();
            const orderedThemedTabs = this.getOrderThemedTabs(this.optionGroup, tabIndex);
            this.babTabs = orderedThemedTabs.filter((d) => d !== undefined);
            if (this.babTabs && this.optionGroup.hasSubGroups(EventOptionGroupType.Period)) {
                const activeTab = this.babTabs.find((p) => p.active === true);
                if (activeTab) {
                    this.setOptionsActive(activeTab.id);
                }
            }
        } else {
            this.babTabs = [];
            if (this.optionGroup.periodName !== '') {
                this.babTabs.push({
                    id: 0,
                    title: this.optionGroup.periodName || '',
                    active: false,
                    attribute: this.optionGroup.id.split('-').pop(),
                    count: this.optionGroup.options?.length,
                });
            }
            this.activeGroup = this.optionGroup;
        }
    }

    private getOrderThemedTabs(optionGroup: EventOptionGroup, tabIndexes: ITabIndex): TabBarItem[] {
        if (!optionGroup || !optionGroup.getSubGroups(EventOptionGroupType.Period)) {
            return [];
        }
        const sportSpecificThemedTabs = this.betBuilderConfig.themedTabs[this.event.sport.id];

        const activeTabIds: string[] = [];
        const tabs: TabBarItem[] = this.optionGroup.getSubGroups(EventOptionGroupType.Period).map((og) => {
            activeTabIds.push(og.id);

            return {
                id: og.detailedGrouping.periodGroup || 0,
                title: og.periodName || '',
                active: false,
                attribute: og.id.split('-').pop(),
                count: og.options?.length,
            };
        });
        tabs.forEach((tab) => (tab.title = sportSpecificThemedTabs?.find((k) => k.key?.toUpperCase() === tab?.title?.toUpperCase())?.name ?? ''));

        const orderedThemedTabs: TabBarItem[] = [];
        sportSpecificThemedTabs?.forEach((i) => {
            const x = tabs.find((y) => y.title === i.name?.trim());
            if (x) {
                orderedThemedTabs.push(x);
            }
        });
        const sortedActiveTabIds: string[] = [];
        orderedThemedTabs.forEach((orderedTab) => {
            const activeTabId = activeTabIds.find((activeTab) => activeTab === orderedTab.attribute?.trim());
            if (activeTabId) {
                sortedActiveTabIds.push(activeTabId);
            }
        });
        if (orderedThemedTabs.length > 0) {
            let currentActiveTabIndex = 0;
            if (tabIndexes.lastActiveTabIndex > -1) {
                currentActiveTabIndex = sortedActiveTabIds.indexOf(tabIndexes.lastActiveTabId);
                if (currentActiveTabIndex === -1) {
                    currentActiveTabIndex = tabIndexes.lastActiveTabIndex;
                    if (currentActiveTabIndex >= activeTabIds.length) {
                        currentActiveTabIndex = activeTabIds.length - 1;
                    }
                }
            }
            orderedThemedTabs[currentActiveTabIndex].active = true;
        }

        return orderedThemedTabs;
    }

    private setOptionsActive(active: number): void {
        if (this.optionGroup.hasSubGroups(EventOptionGroupType.Period)) {
            const activeGroup = this.optionGroup
                .getSubGroups(EventOptionGroupType.Period)
                .find((p) => p.detailedGrouping && p.detailedGrouping.periodGroup === active);
            if (activeGroup) {
                const tabIndex = this.getTabIndex();

                const lastActiveTabIndex = this.babTabs.findIndex((p) => p.id === active);
                tabIndex.lastActiveTabIndex = lastActiveTabIndex === -1 ? 0 : lastActiveTabIndex;
                tabIndex.lastActiveTabId = activeGroup.id;

                this.activeGroup = activeGroup;
            }
        }
    }

    private getTabIndex(): ITabIndex {
        return this.tabKeeper ? this.tabKeeper.getTabIndexByGroup(this.optionGroup) : this.tabIndex;
    }
}
